// src/pages/ReturnsPage.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RotateCcw, Package, CheckCircle, XCircle, Clock, AlertTriangle
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useSearch } from '../context/SearchContext';
import { openCartSidebar } from '../components/CartSidebar';

export default function ReturnsPage() {
  const navigate = useNavigate();
  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const { searchQuery, setSearchQuery } = useSearch();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div className="returns-page">
      <Style />
      
      <Header
        wishlistCount={wishlistCount}
        cartCount={cartCount}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
        onCartClick={openCartSidebar}
        onWishlistClick={() => navigate('/wishlist')}
        onCampaignsClick={() => navigate('/campaigns')}
      />

      {/* Hero */}
      <div className="page-hero">
        <div className="container">
          <RotateCcw size={48} className="hero-icon" />
          <h1>İade ve Değişim</h1>
          <p>Koşulsuz 7 gün iade garantisi</p>
        </div>
      </div>

      {/* Content */}
      <div className="page-content">
        <div className="container">
          {/* Quick Info */}
          <div className="quick-info">
            <div className="info-item">
              <Clock size={32} />
              <h3>7 Gün</h3>
              <p>İade süresi</p>
            </div>
            <div className="info-item">
              <Package size={32} />
              <h3>Ücretsiz</h3>
              <p>İade kargo bedava</p>
            </div>
            <div className="info-item">
              <RotateCcw size={32} />
              <h3>Hızlı</h3>
              <p>3-5 iş günü iade</p>
            </div>
          </div>

          {/* Main Content */}
          <div className="content-section">
            <h2>İade Şartları</h2>
            <div className="content-box">
              <p className="intro-text">
                Ürününüzü teslim aldıktan sonra 7 gün içinde iade edebilirsiniz. 
                İade edilecek ürünün aşağıdaki koşulları sağlaması gerekmektedir:
              </p>
              
              <div className="conditions-grid">
                <div className="condition-card valid">
                  <CheckCircle size={24} />
                  <h4>İade Edilebilir</h4>
                  <ul>
                    <li>Kullanılmamış ve hasarsız olmalı</li>
                    <li>Orijinal ambalajında bulunmalı</li>
                    <li>Fatura ve etiket eksiksiz olmalı</li>
                    <li>Hijyenik bantlar kopuk olmamalı</li>
                    <li>7 gün içinde talep edilmeli</li>
                  </ul>
                </div>
                
                <div className="condition-card invalid">
                  <XCircle size={24} />
                  <h4>İade Edilemez</h4>
                  <ul>
                    <li>Kullanılmış veya hasarlı ürünler</li>
                    <li>Hijyenik bant açılmış ürünler</li>
                    <li>Kişisel bakım ve kozmetik ürünler</li>
                    <li>İç giyim ürünleri</li>
                    <li>Gıda ve içecek ürünleri</li>
                    <li>Özel siparişli ürünler</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="content-section">
            <h2>İade Nasıl Yapılır?</h2>
            <div className="content-box">
              <div className="steps-list">
                <div className="step-item">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h4>İade Talebinde Bulunun</h4>
                    <p>"Hesabım" " Siparişlerim" bölümünden iade etmek istediğiniz siparişi seçin ve "İade Talebi Oluştur" butonuna tıklayın.</p>
                  </div>
                </div>
                
                <div className="step-item">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h4>İade Nedenini Belirtin</h4>
                    <p>İade sebebinizi seçin ve varsa açıklama ekleyin. Fotoğraf yükleyerek süreci hızlandırabilirsiniz.</p>
                  </div>
                </div>
                
                <div className="step-item">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h4>Onay Bekleyin</h4>
                    <p>İade talebiniz 24 saat içinde değerlendirilir. Onay sonrası kargo firması randevu için sizi arayacaktır.</p>
                  </div>
                </div>
                
                <div className="step-item">
                  <div className="step-number">4</div>
                  <div className="step-content">
                    <h4>Ürünü Teslim Edin</h4>
                    <p>Kargo görevlisi ürünü adresinizden alacaktır. Ürünü orijinal ambalajında ve eksiksiz teslim edin.</p>
                  </div>
                </div>
                
                <div className="step-item">
                  <div className="step-number">5</div>
                  <div className="step-content">
                    <h4>İade Onayı</h4>
                    <p>Ürün depomıza ulaştıktan sonra 3-5 iş günü içinde incelenir ve iadəniz onaylanır.</p>
                  </div>
                </div>
                
                <div className="step-item">
                  <div className="step-number">6</div>
                  <div className="step-content">
                    <h4>Para İadesi</h4>
                    <p>İade onayından sonra 5-10 iş günü içinde ödeme iadeniz kartınıza veya hesabınıza yansır.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="content-section">
            <h2>İade Süreleri</h2>
            <div className="content-box">
              <div className="timeline-table">
                <div className="timeline-row">
                  <div className="timeline-step">İade Talebi</div>
                  <div className="timeline-duration">0 gün</div>
                  <div className="timeline-desc">Talep oluşturulur</div>
                </div>
                <div className="timeline-row">
                  <div className="timeline-step">Onay Süreci</div>
                  <div className="timeline-duration">1 gün</div>
                  <div className="timeline-desc">Talep değerlendirilir</div>
                </div>
                <div className="timeline-row">
                  <div className="timeline-step">Kargo Toplama</div>
                  <div className="timeline-duration">1-2 gün</div>
                  <div className="timeline-desc">Ürün alınır</div>
                </div>
                <div className="timeline-row">
                  <div className="timeline-step">Ürün İnceleme</div>
                  <div className="timeline-duration">3-5 gün</div>
                  <div className="timeline-desc">Ürün kontrol edilir</div>
                </div>
                <div className="timeline-row highlight">
                  <div className="timeline-step">Para İadesi</div>
                  <div className="timeline-duration">5-10 gün</div>
                  <div className="timeline-desc">Ödeme iade edilir</div>
                </div>
              </div>
              <p className="timeline-note">
                <AlertTriangle size={16} />
                Toplam süreç ortalama 10-18 iş günü sürmektedir. Yoğun dönemlerde bu süre uzayabilir.
              </p>
            </div>
          </div>

          <div className="content-section">
            <h2>Değişim İşlemleri</h2>
            <div className="content-box">
              <p className="intro-text">
                Ürün değişimi yapmak istiyorsanız, önce iade işlemini başlatmanız gerekmektedir. 
                İade onaylandıktan ve paranız iade edildikten sonra yeni ürün için tekrar sipariş verebilirsiniz.
              </p>
              
              <div className="exchange-info">
                <h4>💡 Hızlı Değişim İpucu</h4>
                <p>
                  Hızlı değişim için iade işlemini başlattıktan hemen sonra yeni ürün siparişi verebilirsiniz. 
                  İade paranız geldiğinde aynı karttan tahsilat yapılacaktır.
                </p>
              </div>
            </div>
          </div>

          <div className="content-section">
            <h2>Sık Sorulan Sorular</h2>
            <div className="content-box">
              <div className="faq-list">
                <div className="faq-item">
                  <h4>Kargo ücreti kim tarafından ödenir?</h4>
                  <p>İade kargo ücreti tarafımızca karşılanır. Kargo görevlisi ürünü adresinizden ücretsiz alır.</p>
                </div>
                <div className="faq-item">
                  <h4>Faturasız ürün iade edilebilir mi?</h4>
                  <p>Hayır, fatura olmadan iade kabul edilmez. Faturanızı muhafaza etmeye özen gösterin.</p>
                </div>
                <div className="faq-item">
                  <h4>Kampanyalı ürünler iade edilebilir mi?</h4>
                  <p>Evet, kampanyalı ürünler de iade edilebilir. Ancak bazı özel kampanyalarda farklı şartlar olabilir.</p>
                </div>
                <div className="faq-item">
                  <h4>Hediye çeki ile alınan ürün iade edilirse ne olur?</h4>
                  <p>Hediye çeki ile alınan ürünlerde iade, hediye çeki bakiyenize yansır.</p>
                </div>
                <div className="faq-item">
                  <h4>Yurtdışından gelen ürün iade edilebilir mi?</h4>
                  <p>Şu an sadece Türkiye içi siparişler kabul edildiği için yurtdışı iade bulunmamaktadır.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Warning Box */}
          <div className="warning-box">
            <AlertTriangle size={32} />
            <h3>Önemli Uyarılar</h3>
            <ul>
              <li>7 günlük süre ürünün tarafınıza teslim edildiği tarihten itibaren başlar.</li>
              <li>İade edilecek ürünler hijyen kurallarına uygun olmalıdır.</li>
              <li>Ambalaj hasarları nedeniyle iade kabul edilmeyebilir.</li>
              <li>Tüketici Kanunu gereği bazı ürünler iade edilemez.</li>
              <li>Özel üretim ve sipariş üzerine hazırlanan ürünler iade edilemez.</li>
            </ul>
          </div>

          {/* CTA */}
          <div className="cta-box">
            <h3>İade işlemi için yardıma mı ihtiyacınız var?</h3>
            <p>Müşteri hizmetlerimiz size yardımcı olmaktan mutluluk duyar</p>
            <div className="cta-buttons">
              <button className="btn-primary" onClick={() => navigate('/contact')}>
                İletişime Geçin
              </button>
              <button className="btn-secondary" onClick={() => navigate('/faq')}>
                SSS
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function Style() {
  return (
    <style>{`
      * { margin: 0; padding: 0; box-sizing: border-box; }
      
      .returns-page {
        min-height: 100vh;
        background: #f8f9fa;
      }

      .container {
        max-width: 1320px;
        margin: 0 auto;
        padding: 0 20px;
      }

      .page-hero {
        background: linear-gradient(135deg, #E5332A 0%, #c72a22 100%);
        padding: 80px 0;
        text-align: center;
        color: #fff;
      }

      .hero-icon {
        margin-bottom: 24px;
        opacity: 0.9;
      }

      .page-hero h1 {
        font-size: 48px;
        font-weight: 800;
        margin-bottom: 16px;
      }

      .page-hero p {
        font-size: 18px;
        opacity: 0.9;
      }

      .page-content {
        padding: 60px 0;
      }

      /* Quick Info */
      .quick-info {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 24px;
        margin-bottom: 60px;
      }

      .info-item {
        background: #fff;
        padding: 32px;
        border-radius: 16px;
        text-align: center;
        box-shadow: 0 2px 8px rgba(0,0,0,0.04);
      }

      .info-item svg {
        color: #E5332A;
        margin-bottom: 16px;
      }

      .info-item h3 {
        font-size: 24px;
        font-weight: 700;
        margin-bottom: 8px;
        color: #2B2B2B;
      }

      .info-item p {
        color: #868e96;
      }

      /* Content Section */
      .content-section {
        margin-bottom: 48px;
      }

      .content-section h2 {
        font-size: 32px;
        font-weight: 800;
        margin-bottom: 24px;
        color: #2B2B2B;
      }

      .content-box {
        background: #fff;
        padding: 32px;
        border-radius: 16px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.04);
      }

      .intro-text {
        color: #495057;
        line-height: 1.8;
        margin-bottom: 24px;
      }

      /* Conditions Grid */
      .conditions-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 24px;
      }

      .condition-card {
        padding: 24px;
        border-radius: 12px;
        border: 2px solid;
      }

      .condition-card.valid {
        background: #f0fdf9;
        border-color: #06d6a0;
      }

      .condition-card.invalid {
        background: #fef2f2;
        border-color: #ef4444;
      }

      .condition-card svg {
        margin-bottom: 16px;
      }

      .condition-card.valid svg {
        color: #06d6a0;
      }

      .condition-card.invalid svg {
        color: #ef4444;
      }

      .condition-card h4 {
        font-size: 18px;
        margin-bottom: 16px;
        color: #2B2B2B;
      }

      .condition-card ul {
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .condition-card li {
        color: #495057;
        padding-left: 24px;
        position: relative;
        line-height: 1.6;
      }

      .condition-card.valid li::before {
        content: '✓';
        position: absolute;
        left: 0;
        color: #06d6a0;
        font-weight: 700;
      }

      .condition-card.invalid li::before {
        content: '✗';
        position: absolute;
        left: 0;
        color: #ef4444;
        font-weight: 700;
      }

      /* Steps List */
      .steps-list {
        display: flex;
        flex-direction: column;
        gap: 24px;
      }

      .step-item {
        display: flex;
        gap: 20px;
        align-items: flex-start;
      }

      .step-number {
        width: 48px;
        height: 48px;
        background: linear-gradient(135deg, #E5332A, #ff4436);
        color: #fff;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 20px;
        flex-shrink: 0;
      }

      .step-content h4 {
        font-size: 18px;
        margin-bottom: 8px;
        color: #2B2B2B;
      }

      .step-content p {
        color: #495057;
        line-height: 1.7;
      }

      /* Timeline Table */
      .timeline-table {
        border-radius: 12px;
        overflow: hidden;
        border: 2px solid #e9ecef;
      }

      .timeline-row {
        display: grid;
        grid-template-columns: 2fr 1fr 2fr;
        padding: 16px;
        border-bottom: 1px solid #e9ecef;
        align-items: center;
      }

      .timeline-row:last-child {
        border-bottom: none;
      }

      .timeline-row.highlight {
        background: #fff5f5;
      }

      .timeline-step {
        font-weight: 600;
        color: #2B2B2B;
      }

      .timeline-duration {
        text-align: center;
        color: #E5332A;
        font-weight: 700;
      }

      .timeline-desc {
        color: #868e96;
        font-size: 14px;
      }

      .timeline-note {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: 16px;
        padding: 12px;
        background: #fffbeb;
        border-radius: 8px;
        color: #d97706;
        font-size: 14px;
      }

      /* Exchange Info */
      .exchange-info {
        padding: 24px;
        background: #eff6ff;
        border-left: 4px solid #3b82f6;
        border-radius: 8px;
        margin-top: 24px;
      }

      .exchange-info h4 {
        font-size: 16px;
        margin-bottom: 12px;
        color: #2B2B2B;
      }

      .exchange-info p {
        color: #495057;
        line-height: 1.7;
      }

      /* FAQ List */
      .faq-list {
        display: flex;
        flex-direction: column;
        gap: 24px;
      }

      .faq-item h4 {
        font-size: 16px;
        margin-bottom: 8px;
        color: #2B2B2B;
      }

      .faq-item p {
        color: #495057;
        line-height: 1.7;
      }

      /* Warning Box */
      .warning-box {
        background: #fffbeb;
        border: 2px solid #f59e0b;
        border-radius: 16px;
        padding: 32px;
        margin-top: 48px;
      }

      .warning-box svg {
        color: #f59e0b;
        margin-bottom: 16px;
      }

      .warning-box h3 {
        font-size: 24px;
        margin-bottom: 16px;
        color: #2B2B2B;
      }

      .warning-box ul {
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .warning-box li {
        color: #495057;
        padding-left: 24px;
        position: relative;
        line-height: 1.6;
      }

      .warning-box li::before {
        content: '⚠';
        position: absolute;
        left: 0;
        color: #f59e0b;
      }

      /* CTA Box */
      .cta-box {
        background: linear-gradient(135deg, #2B2B2B, #1a1a1a);
        padding: 48px;
        border-radius: 24px;
        text-align: center;
        color: #fff;
        margin-top: 60px;
      }

      .cta-box h3 {
        font-size: 28px;
        margin-bottom: 12px;
      }

      .cta-box p {
        font-size: 16px;
        opacity: 0.8;
        margin-bottom: 32px;
      }

      .cta-buttons {
        display: flex;
        gap: 16px;
        justify-content: center;
        flex-wrap: wrap;
      }

      .btn-primary, .btn-secondary {
        padding: 14px 32px;
        border-radius: 12px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.3s;
        border: 2px solid transparent;
      }

      .btn-primary {
        background: #E5332A;
        color: #fff;
      }

      .btn-primary:hover {
        background: #c72a22;
        transform: translateY(-2px);
      }

      .btn-secondary {
        background: transparent;
        color: #fff;
        border-color: #fff;
      }

      .btn-secondary:hover {
        background: #fff;
        color: #2B2B2B;
      }

      /* Responsive */
      @media (max-width: 992px) {
        .quick-info {
          grid-template-columns: 1fr;
        }

        .conditions-grid {
          grid-template-columns: 1fr;
        }

        .timeline-row {
          grid-template-columns: 1fr;
          gap: 8px;
        }

        .timeline-duration {
          text-align: left;
        }
      }

      @media (max-width: 768px) {
        .page-hero h1 {
          font-size: 32px;
        }

        .content-section h2 {
          font-size: 24px;
        }

        .content-box {
          padding: 24px;
        }

        .cta-box {
          padding: 32px 24px;
        }

        .cta-buttons {
          flex-direction: column;
        }

        .btn-primary, .btn-secondary {
          width: 100%;
        }
      }
    `}</style>
  );
}